import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Star, Flame, Trophy } from 'lucide-react';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';

// Composant principal de la barre XP - Design minimaliste
export const XPBar = memo(function XPBar({
  todayXP,
  totalXP,
  level,
  levelProgress,
  currentStreak,
  showDetails = false
}) {
  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedProgress(levelProgress);
    }, 100);
    return () => clearTimeout(timer);
  }, [levelProgress]);

  return (
    <div className="xp-bar-container">
      <div className="flex items-center gap-2">
        {/* Niveau */}
        <motion.div
          className="flex items-center gap-1 px-2 py-1 glass-card text-zinc-300"
          whileTap={{ scale: 0.95 }}
        >
          <Star className="w-3 h-3 text-zinc-500" />
          <span className="text-xs font-medium">{level}</span>
        </motion.div>

        {/* Barre de progression */}
        <div className="flex-grow h-1.5 bg-zinc-800 rounded-full overflow-hidden relative">
          <motion.div
            className="h-full bg-gradient-to-r from-zinc-600 to-zinc-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${animatedProgress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        {/* XP du jour */}
        <div className="flex items-center gap-1 text-zinc-400">
          <Zap className="w-3 h-3" />
          <span className="text-xs font-medium">+{todayXP}</span>
        </div>

        {/* Streak */}
        {currentStreak > 0 && (
          <motion.div
            className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded text-amber-400"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            <Flame className="w-3 h-3" />
            <span className="text-xs font-medium">{currentStreak}</span>
          </motion.div>
        )}
      </div>

      {/* Détails étendus */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 flex justify-between text-[10px] text-zinc-500"
          >
            <span>Total: {totalXP} XP</span>
            <span>Niveau {level} → {level + 1}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

// Animation de gain d'XP - Popup flottant modernisé
export const XPGainAnimation = memo(function XPGainAnimation({ amount, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 1800);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className={cn(
        'fixed top-20 left-1/2 flex items-center gap-2 px-4 py-2',
        'glass-elevated shadow-2xl',
        Z_CLASS.XP_ANIMATION
      )}
      initial={{ opacity: 0, y: -20, x: '-50%' }}
      animate={{ opacity: 1, y: 0, x: '-50%' }}
      exit={{ opacity: 0, y: -20, x: '-50%' }}
    >
      <div className="w-8 h-8 bg-amber-500/20 rounded flex items-center justify-center">
        <Zap className="w-4 h-4 text-amber-400" />
      </div>
      <div>
        <span className="text-lg font-semibold text-zinc-100">+{amount}</span>
        <span className="text-xs text-zinc-500 ml-1">XP</span>
      </div>
    </motion.div>
  );
});

// Animation de Level Up - Design épuré
export const LevelUpAnimation = memo(function LevelUpAnimation({ level, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className={cn(
        'fixed inset-0 flex items-center justify-center',
        'glass-overlay',
        Z_CLASS.LEVEL_UP
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="text-center"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        <motion.div
          className="w-20 h-20 mx-auto glass-card flex items-center justify-center mb-6"
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 0.5, repeat: 2 }}
        >
          <Trophy className="w-10 h-10 text-amber-400" />
        </motion.div>

        <motion.p
          className="text-xs font-medium text-zinc-500 uppercase tracking-widest mb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          Niveau supérieur
        </motion.p>

        <motion.div
          className="text-6xl font-bold text-zinc-100 mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.3 }}
        >
          {level}
        </motion.div>

        <motion.p
          className="text-sm text-zinc-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Continue comme ça !
        </motion.p>
      </motion.div>
    </motion.div>
  );
});

// Animation de badge débloqué - Design moderne
export const BadgeUnlockAnimation = memo(function BadgeUnlockAnimation({ badge, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className={cn(
        'fixed inset-0 flex items-center justify-center',
        'glass-overlay',
        Z_CLASS.BADGE_UNLOCK
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="text-center max-w-xs"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        <motion.div
          className="w-24 h-24 mx-auto glass-card flex items-center justify-center mb-6 text-5xl"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
        >
          {badge.icon}
        </motion.div>

        <motion.p
          className="text-xs font-medium text-zinc-500 uppercase tracking-widest mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Nouveau Badge
        </motion.p>

        <motion.h3
          className="text-xl font-semibold text-zinc-100 mb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {badge.name}
        </motion.h3>

        <motion.p
          className="text-sm text-zinc-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {badge.description}
        </motion.p>
      </motion.div>
    </motion.div>
  );
});

// Composant de confettis - Version optimisée
export const Confetti = memo(function Confetti({ active, onComplete }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (active) {
      const colors = ['#71717a', '#a1a1aa', '#d4d4d8', '#52525b', '#3f3f46'];
      const newParticles = Array.from({ length: 40 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 3,
        rotation: Math.random() * 360
      }));
      setParticles(newParticles);

      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [active, onComplete]);

  if (!active || particles.length === 0) return null;

  return (
    <div aria-hidden="true" className={cn('fixed inset-0 pointer-events-none overflow-hidden', Z_CLASS.CONFETTI)}>
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute top-0 rounded-sm"
          style={{
            left: `${p.x}%`,
            backgroundColor: p.color,
            width: p.size,
            height: p.size,
          }}
          initial={{ y: -20, rotate: 0, opacity: 1 }}
          animate={{
            y: window.innerHeight + 50,
            rotate: p.rotation + 360,
            opacity: 0
          }}
          transition={{
            duration: 2,
            delay: p.delay,
            ease: 'easeIn'
          }}
        />
      ))}
    </div>
  );
});

// Message d'encouragement flottant - Design toast moderne
export const EncouragementToast = memo(function EncouragementToast({ message, type = 'success', onComplete }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2800);
    return () => clearTimeout(timer);
  }, [onComplete]);

  const typeStyles = {
    success: 'border-emerald-500/30 bg-emerald-500/10',
    vital: 'border-red-500/30 bg-red-500/10',
    streak: 'border-amber-500/30 bg-amber-500/10',
    info: 'border-zinc-500/30 bg-zinc-500/10'
  };

  const textStyles = {
    success: 'text-emerald-300',
    vital: 'text-red-300',
    streak: 'text-amber-300',
    info: 'text-zinc-300'
  };

  return (
    <motion.div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        'fixed bottom-24 left-1/2 px-4 py-3 rounded-lg border backdrop-blur-sm',
        typeStyles[type] || typeStyles.info,
        Z_CLASS.ENCOURAGEMENT_TOAST
      )}
      initial={{ opacity: 0, y: 20, x: '-50%' }}
      animate={{ opacity: 1, y: 0, x: '-50%' }}
      exit={{ opacity: 0, y: 20, x: '-50%' }}
    >
      <p className={cn('text-sm font-medium', textStyles[type] || textStyles.info)}>
        {message}
      </p>
    </motion.div>
  );
});

// Affichage des badges - Grille moderne
export const BadgeDisplay = memo(function BadgeDisplay({ badges, unlockedIds = [] }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {Object.values(badges).map(badge => {
        const isUnlocked = unlockedIds.includes(badge.id);
        return (
          <motion.div
            key={badge.id}
            className={cn(
              'flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors',
              isUnlocked
                ? 'glass-card'
                : 'bg-zinc-900/50 border-zinc-800/50 opacity-50'
            )}
            whileHover={isUnlocked ? { scale: 1.05 } : {}}
            title={badge.description}
          >
            <span className="text-2xl">{isUnlocked ? badge.icon : '🔒'}</span>
            <span className="text-[9px] text-zinc-500 text-center truncate w-full">
              {badge.name}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
});

// Indicateur de streak - Design épuré
export const StreakIndicator = memo(function StreakIndicator({ streak, longestStreak }) {
  const milestones = [3, 7, 14, 30, 100];
  const nextMilestone = milestones.find(m => m > streak) || milestones[milestones.length - 1];
  const progress = (streak / nextMilestone) * 100;

  return (
    <div className="p-4 glass-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
          <Flame className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <span className="block text-lg font-semibold text-zinc-100">{streak} jours</span>
          <span className="block text-[10px] text-zinc-500">Série en cours</span>
        </div>
      </div>

      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-2">
        <motion.div
          className="h-full bg-gradient-to-r from-amber-600 to-amber-400 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(progress, 100)}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="flex justify-between text-[10px] text-zinc-500">
        <span>Prochain: {nextMilestone} jours</span>
        <span>Record: {longestStreak}</span>
      </div>
    </div>
  );
});

export default XPBar;
